import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Paper, 
  Button, 
  Breadcrumbs,
  Link as MuiLink,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemText,
  Rating,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Snackbar,
  Alert
} from '@mui/material';
import { 
  ShoppingCart as CartIcon,
  Favorite as FavoriteIcon,
  Share as ShareIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../contexts/CartContext';

const ProductDetail = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const [tabValue, setTabValue] = useState(0);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Mock data for product details
  useEffect(() => {
    // In a real app, this would be an API call
    setTimeout(() => {
      setProduct({
        id: parseInt(id),
        name: `${id === '1' ? 'Fresh Tomatoes' : id === '2' ? 'Organic Potatoes' : 'Green Spinach'}`,
        description: 'High-quality fresh produce sourced directly from local farmers. Our products are carefully selected to ensure the best quality and taste. Perfect for your daily cooking needs and healthy lifestyle.',
        price: 120,
        salePrice: 100,
        unit: 'kg',
        rating: 4.8,
        reviewCount: 65,
        stock: 50,
        images: [
          `https://source.unsplash.com/random/600x400?${id === '1' ? 'tomatoes' : id === '2' ? 'potatoes' : 'spinach'}&sig=1`,
          `https://source.unsplash.com/random/600x400?${id === '1' ? 'tomatoes' : id === '2' ? 'potatoes' : 'spinach'}&sig=2`,
          `https://source.unsplash.com/random/600x400?${id === '1' ? 'tomatoes' : id === '2' ? 'potatoes' : 'spinach'}&sig=3`,
        ],
        store: {
          id: 1,
          name: 'Fresh Vegetables Store',
          rating: 4.5
        },
        bazar: {
          id: 1,
          name: 'Chanchkoir Bazar'
        },
        category: 'Vegetables',
        attributes: [
          { name: 'Origin', value: 'Local' },
          { name: 'Freshness', value: 'Same day harvest' },
          { name: 'Organic', value: 'Yes' },
          { name: 'Storage', value: 'Refrigerate' }
        ]
      });
      
      setLoading(false);
    }, 500);
  }, [id]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleQuantityChange = (event) => {
    const value = parseInt(event.target.value);
    if (!isNaN(value) && value > 0 && value <= product.stock) {
      setQuantity(value);
    }
  };

  const increaseQuantity = () => {
    if (quantity < product.stock) {
      setQuantity(quantity + 1);
    }
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleAddToCart = () => {
    // Use the cart context to add the product
    addToCart(
      { 
        id: product.id, 
        name: product.name, 
        price: product.salePrice || product.price,
        image: product.images[0],
        store: product.store.name,
        stock: product.stock
      }, 
      quantity
    );
    
    // Show success message
    setSnackbar({
      open: true,
      message: `${product.name} added to cart!`,
      severity: 'success'
    });
    
    setTimeout(() => {
      setSnackbar(prev => ({...prev, open: false}));
    }, 3000);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({...prev, open: false}));
  };

  if (loading) {
    return (
      <>
        <Header />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 8, minHeight: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography>Loading product details...</Typography>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
          <MuiLink component={Link} to="/" color="inherit">
            Home
          </MuiLink>
          <MuiLink component={Link} to="/bazars" color="inherit">
            Bazars
          </MuiLink>
                      <MuiLink component={Link} to={`/bazars/${product.bazar.slug || 'chanchkoir-bazar'}`} color="inherit">
            {product.bazar.name}
          </MuiLink>
          <MuiLink component={Link} to={`/stores/${product.store.id}`} color="inherit">
            {product.store.name}
          </MuiLink>
          <Typography color="text.primary">{product.name}</Typography>
        </Breadcrumbs>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardMedia
                component="img"
                height="400"
                image={product.images[0]}
                alt={product.name}
              />
              <Box sx={{ display: 'flex', p: 2 }}>
                {product.images.map((image, index) => (
                  <Box 
                    key={index}
                    component="img"
                    src={image}
                    alt={`${product.name} view ${index + 1}`}
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      objectFit: 'cover', 
                      borderRadius: 1,
                      mr: 1,
                      border: index === 0 ? '2px solid #5e35b1' : '1px solid #e0e0e0',
                      cursor: 'pointer'
                    }}
                  />
                ))}
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Chip label={product.category} color="primary" size="small" sx={{ mb: 2 }} />
              <Typography variant="h4" component="h1" gutterBottom>
                {product.name}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Rating value={product.rating} precision={0.1} readOnly />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  ({product.reviewCount} reviews)
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                <Typography variant="body2">
                  Store: <MuiLink component={Link} to={`/stores/${product.store.id}`}>{product.store.name}</MuiLink>
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
                {product.salePrice < product.price ? (
                  <>
                    <Typography variant="h4" color="primary" sx={{ mr: 2 }}>
                      ৳{product.salePrice}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                      ৳{product.price}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="h4" color="primary">
                    ৳{product.price}
                  </Typography>
                )}
                <Typography variant="body1" sx={{ ml: 1 }}>
                  per {product.unit}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body1" paragraph>
                {product.description}
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Availability: <Chip 
                    label={product.stock > 0 ? 'In Stock' : 'Out of Stock'} 
                    color={product.stock > 0 ? 'success' : 'error'} 
                    size="small" 
                  />
                </Typography>
                {product.stock > 0 && (
                  <Typography variant="body2">
                    {product.stock} {product.unit}s available
                  </Typography>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mr: 2 }}>
                  Quantity:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton onClick={decreaseQuantity} disabled={quantity <= 1}>
                    <RemoveIcon />
                  </IconButton>
                  <TextField
                    value={quantity}
                    onChange={handleQuantityChange}
                    inputProps={{ min: 1, max: product.stock, style: { textAlign: 'center' } }}
                    sx={{ width: 60 }}
                  />
                  <IconButton onClick={increaseQuantity} disabled={quantity >= product.stock}>
                    <AddIcon />
                  </IconButton>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', mt: 3 }}>
                <Button 
                  variant="contained"
                  size="large"
                  startIcon={<CartIcon />}
                  sx={{ mr: 2, flexGrow: 1 }}
                  onClick={handleAddToCart}
                >
                  Add to Cart
                </Button>
                <IconButton color="primary" aria-label="add to wishlist">
                  <FavoriteIcon />
                </IconButton>
                <IconButton color="primary" aria-label="share">
                  <ShareIcon />
                </IconButton>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{ mt: 4 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant="fullWidth"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Description" />
            <Tab label="Specifications" />
            <Tab label="Reviews" />
          </Tabs>
          <Box sx={{ p: 3 }}>
            {tabValue === 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>Product Description</Typography>
                <Typography paragraph>{product.description}</Typography>
                <Typography paragraph>
                  Our products are carefully selected to ensure freshness and quality. We work directly with local farmers to provide the best produce for your daily needs. All of our products are hand-picked and delivered to our stores on the same day they're harvested to ensure maximum freshness.
                </Typography>
                <Typography paragraph>
                  We believe in sustainable farming practices and supporting local communities. When you purchase from us, you're not only getting high-quality products but also supporting local farmers and sustainable agriculture.
                </Typography>
              </Box>
            )}
            {tabValue === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>Product Specifications</Typography>
                <TableContainer>
                  <Table>
                    <TableBody>
                      {product.attributes.map((attribute, index) => (
                        <TableRow key={index}>
                          <TableCell component="th" scope="row" sx={{ width: '30%', fontWeight: 'bold' }}>
                            {attribute.name}
                          </TableCell>
                          <TableCell>{attribute.value}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                          Unit
                        </TableCell>
                        <TableCell>{product.unit}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
            {tabValue === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom>Customer Reviews</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Rating value={product.rating} precision={0.1} readOnly size="large" />
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {product.rating} out of 5
                  </Typography>
                </Box>
                <Typography paragraph>
                  Based on {product.reviewCount} reviews
                </Typography>
                <Divider sx={{ my: 2 }} />
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Very fresh produce, highly recommended!"
                      secondary={
                        <>
                          <Rating value={5} size="small" readOnly />
                          <Typography variant="body2">John D. - 3 days ago</Typography>
                        </>
                      } 
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="Good quality but delivery was a bit delayed"
                      secondary={
                        <>
                          <Rating value={4} size="small" readOnly />
                          <Typography variant="body2">Sarah M. - 1 week ago</Typography>
                        </>
                      } 
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="Perfect for my daily cooking needs"
                      secondary={
                        <>
                          <Rating value={5} size="small" readOnly />
                          <Typography variant="body2">Rahim K. - 2 weeks ago</Typography>
                        </>
                      } 
                    />
                  </ListItem>
                </List>
              </Box>
            )}
          </Box>
        </Paper>

        <Box sx={{ mt: 6 }}>
          <Typography variant="h5" gutterBottom>Related Products</Typography>
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 3,
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    height="140"
                    image={`https://source.unsplash.com/random/300x200?vegetables&sig=${item}`}
                    alt={`Related product ${item}`}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography gutterBottom variant="h6" component="h3">
                      {['Red Onions', 'Carrots', 'Cucumber', 'Bell Peppers'][item - 1]}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Fresh Vegetables Store
                    </Typography>
                    <Typography variant="h6" color="primary">
                      ৳{[90, 70, 60, 110][item - 1]}
                    </Typography>
                  </CardContent>
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button 
                      size="small" 
                      variant="contained" 
                      fullWidth
                      startIcon={<CartIcon />}
                    >
                      Add to Cart
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
      <Footer />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductDetail;
